import { useEffect, useState } from "react";
import { CheckCircle2, Home, FileText, Building2, Clock, Hammer, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { Field, Area, Selectable } from "@/components/forms/Fields";
import { submitLead, track } from "@/lib/api";
import useSeo from "@/lib/useSeo";

const LOOKING_FOR = [
  { icon: Hammer, text: "Renovation opportunities" },
  { icon: Home, text: "Dated properties" },
  { icon: FileText, text: "Probate / inherited property" },
  { icon: Building2, text: "Vacant property" },
  { icon: Clock, text: "Properties needing significant modernisation" },
  { icon: TrendingUp, text: "Properties with development potential" },
];

export default function EstateAgents() {
  const [form, setForm] = useState({ agent_name: "", agency: "", email: "", phone: "", address: "", asking_price: "", expected_price: "", property_type: "", why_suits: "", listing_link: "", additional: "", consent: false, website: "" });
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  useSeo({ title: "For Estate Agents | Emerald Crown Property Investments Nottinghamshire", description: "Your local Nottinghamshire buyer for properties that need a different approach. Send us renovation, probate, vacant and development opportunities.", path: "/estate-agents" });
  useEffect(() => { track("page_view", { page: "estate_agents" }); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agent_name || !form.agency || !form.email || !form.phone) return toast.error("Please complete the required fields.");
    if (!form.consent) return toast.error("Please provide your consent to continue.");
    setBusy(true);
    try {
      await submitLead({
        lead_type: "Estate Agent", full_name: form.agent_name, email: form.email, phone: form.phone,
        address: form.address, consent: form.consent, website: form.website, expected_price: form.expected_price,
        notes: form.additional,
        property_details: { agency: form.agency, asking_price: form.asking_price, expected_price: form.expected_price, property_type: form.property_type, why_suits: form.why_suits, listing_link: form.listing_link },
      });
      track("agent_form_submit");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="pt-20">
      <section className="relative">
        <img src="https://images.unsplash.com/photo-1607900840367-a83dcd63296d?auto=format&fit=crop&q=85&w=1600" alt="Residential street" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-36">
          <div className="max-w-2xl">
            <span className="eyebrow text-brass">For estate agents</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-serif font-semibold text-white leading-tight">Work with us</h1>
            <p className="mt-5 text-lg text-emerald-50/90">Your local buyer for properties that need a different approach.</p>
            <a href="#agent-form"><Button className="mt-8 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-8 h-12" data-testid="agent-hero-cta">Send us an opportunity</Button></a>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          <h2 className="text-3xl font-serif text-slate-900">We actively look for</h2>
          <p className="mt-3 text-slate-600">We're a reliable, professional buyer for the properties that can be harder to place with a typical retail buyer.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {LOOKING_FOR.map((l, i) => (
            <Reveal key={l.text} delay={i * 70}>
              <div className="flex items-center gap-3.5 bg-white rounded-xl border border-slate-200/80 p-5 h-full">
                <span className="grid place-items-center w-11 h-11 rounded-lg bg-emerald-50 shrink-0"><l.icon className="w-5 h-5 text-emerald-800" /></span>
                <p className="text-slate-700 font-medium">{l.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 text-slate-600 max-w-2xl">We also welcome properties that may not appeal to the typical retail buyer — if you think it might suit us, we'd rather you asked.</p>
      </section>

      <section id="agent-form" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {submitted ? (
          <div data-testid="agent-form-success" className="text-center bg-white rounded-2xl border border-slate-200/80 p-12">
            <CheckCircle2 className="w-14 h-14 text-emerald-700 mx-auto" />
            <h2 className="mt-5 text-2xl font-serif text-slate-900">Thanks — opportunity received.</h2>
            <p className="mt-3 text-slate-600">We'll review the details and get back to you promptly.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200/80 p-6 sm:p-8">
            <span className="eyebrow">Submit a property</span>
            <h2 className="mt-4 text-3xl font-serif text-slate-900">Send us an opportunity</h2>
            <form onSubmit={submit} className="mt-7 space-y-5" data-testid="agent-form">
              <input type="text" name="website" value={form.website} onChange={(e) => set("website")(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Agent name" required value={form.agent_name} onChange={set("agent_name")} testId="agent-name" />
                <Field label="Agency" required value={form.agency} onChange={set("agency")} testId="agent-agency" />
                <Field label="Email" type="email" required value={form.email} onChange={set("email")} testId="agent-email" />
                <Field label="Phone" required value={form.phone} onChange={set("phone")} testId="agent-phone" />
              </div>
              <Field label="Property address" value={form.address} onChange={set("address")} testId="agent-address" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Asking price" value={form.asking_price} onChange={set("asking_price")} testId="agent-asking-price" />
                <Field label="Expected / achievable price" value={form.expected_price} onChange={set("expected_price")} testId="agent-expected-price" />
                <Selectable label="Property type" value={form.property_type} onChange={set("property_type")} options={["Terraced", "Semi-detached", "Detached", "Bungalow", "Flat / apartment", "Other"]} testId="agent-property-type" />
                <Field label="Link to listing" value={form.listing_link} onChange={set("listing_link")} placeholder="https://…" testId="agent-listing-link" />
              </div>
              <Area label="Why the property may suit us" value={form.why_suits} onChange={set("why_suits")} placeholder="e.g. probate sale, needs full refurbishment, struggled to sell…" testId="agent-why" />
              <Area label="Additional information" value={form.additional} onChange={set("additional")} testId="agent-additional" />
              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input type="checkbox" checked={form.consent} onChange={(e) => set("consent")(e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-800" data-testid="agent-consent" />
                <span>I confirm I'm authorised to share this property information and consent to being contacted about it. *</span>
              </label>
              <Button type="submit" disabled={busy} data-testid="agent-submit" className="bg-emerald-900 hover:bg-emerald-800 text-white rounded-full px-8 h-12">{busy ? "Sending…" : "Send us this opportunity"}</Button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
